// AI models — each tenant (customer login) connects their OWN OpenRouter account and picks the
// model Grow My X uses for each kind of AI work. The platform never pays for a tenant's AI.
//
// GET    /api/ai                       → AiSettingsRes
// PUT    /api/ai                       AiSettingsPut → AiSettingsRes   (models per task, image analysis)
// GET    /api/ai/models                → AiModelsRes                   (OpenRouter catalog + recommendations)
// POST   /api/ai/openrouter/start      → AiOAuthStartRes               (OpenRouter sign-in URL, PKCE)
// POST   /api/ai/openrouter/callback   AiOAuthCallbackReq → AiSettingsRes
// PUT    /api/ai/openrouter/key        AiKeyReq → AiSettingsRes        (paste an existing key instead)
// DELETE /api/ai/openrouter            → AiSettingsRes                 (disconnect)
// POST   /api/ai/test                  AiTestReq → AiTestRes           (one tiny real call with the picked model)

/** The kinds of AI work a tenant picks a model for. */
export type AiTask = "messaging" | "comments" | "vision" | "video" | "content";
export const AI_TASKS: readonly AiTask[] = ["messaging", "comments", "vision", "video", "content"];

export interface AiTaskInfo {
  label: string;
  description: string;
  /** Where in Grow My X this model is used. */
  usedFor: string;
  /** The picked model must accept images. */
  needsVision: boolean;
  /** The picked model must accept video (with its sound). */
  needsVideo?: boolean;
  /** Typical tokens for ONE action, for "≈ $x per 1,000" estimates. */
  profile: { inputTokens: number; outputTokens: number; images: number };
  /** "DMs" in "≈ $0.20 per 1,000 DMs". */
  unit: string;
}

export const AI_TASK_INFO: Record<AiTask, AiTaskInfo> = {
  messaging: {
    label: "Messaging",
    description: "Writes DMs in your brand voice.",
    usedFor: "Autoresponder replies, outreach openers and follow-ups, reply suggestions in the X overlay.",
    needsVision: false,
    profile: { inputTokens: 1800, outputTokens: 120, images: 0 },
    unit: "DMs",
  },
  comments: {
    label: "Comments",
    description: "Sorts comments into leads, questions and spam, and writes replies and comments.",
    usedFor: "Comment AI on your own posts and the comments Engage leaves on other people's posts.",
    needsVision: false,
    profile: { inputTokens: 450, outputTokens: 100, images: 0 },
    unit: "comments",
  },
  vision: {
    label: "Image analysis",
    description: "Looks at pictures: posts before Spotlight comments on them, the post image before other comments, your own posts for Auto-Caption, and someone's latest posts for a Profile Snapshot.",
    usedFor: "Spotlight's \"Look at the post first\" (a short note on what the photo, slides or reel cover show — the Comments model writes from it), comments when \"Look at the post image\" is on (replaces the Comments model on posts with an image), Auto-Caption, and Profile Snapshots (reading a person's last 12 posts before you DM them).",
    needsVision: true,
    profile: { inputTokens: 1400, outputTokens: 100, images: 1 },
    unit: "posts",
  },
  video: {
    label: "Video analysis",
    description: "Watches reels and videos with their sound: what happens, what's said, the text on screen and the music.",
    usedFor: "Spotlight's \"Watch reels and videos with sound\" (a short note on the whole video — the Comments model writes from it).",
    needsVision: false,
    needsVideo: true,
    // A ~1-minute reel at low resolution: about 100 tokens per second of video + sound.
    profile: { inputTokens: 6500, outputTokens: 160, images: 0 },
    unit: "videos",
  },
  content: {
    label: "Captions & content",
    description: "Writes post captions and hashtags.",
    usedFor: "Caption writing in the Scheduler.",
    needsVision: false,
    profile: { inputTokens: 1500, outputTokens: 500, images: 0 },
    unit: "captions",
  },
};

/** Tokens one image usually costs when a model bills images as input tokens (~1080×1080). */
export const AI_IMAGE_TOKENS_ESTIMATE = 1600;

/** One model from the OpenRouter catalog, trimmed for the picker. Prices are USD per 1M tokens. */
export interface AiModelInfo {
  id: string;
  name: string;
  /** "anthropic" from "anthropic/claude-sonnet-5.5". */
  vendor: string;
  contextLength: number;
  inputPrice: number;
  outputPrice: number;
  /** USD per image when the model prices images separately, else null (billed as input tokens). */
  imagePrice: number | null;
  vision: boolean;
  /** Accepts video (OpenRouter "video" input). */
  video: boolean;
  /** Thinks before answering (Grow My X asks for the lowest effort it allows). */
  reasoning: boolean;
  free: boolean;
  /** Unix ms the model was added to OpenRouter. */
  createdAt: number;
}

export type AiRecommendationKind = "best" | "cheapest";

export interface AiRecommendation {
  kind: AiRecommendationKind;
  modelId: string;
  why: string;
}

export interface AiModelsRes {
  models: AiModelInfo[];
  /** Resolved against the live catalog — a retired candidate falls through to the next one. */
  recommendations: Record<AiTask, AiRecommendation[]>;
  fetchedAt: number;
}

/** Candidates in order of preference; the first one OpenRouter still lists wins. */
export const AI_RECOMMENDED: Record<AiTask, Record<AiRecommendationKind, { candidates: string[]; why: string }>> = {
  messaging: {
    best: {
      candidates: ["anthropic/claude-sonnet-5.5", "anthropic/claude-sonnet-5", "anthropic/claude-sonnet-4.6"],
      why: "The most natural, on-voice DMs. Follows your brand voice and the conversation closely.",
    },
    cheapest: {
      candidates: ["anthropic/claude-haiku-5.5", "openai/gpt-6-luna", "anthropic/claude-haiku-4.5"],
      why: "Still sounds like a person, at about a twentieth of the price.",
    },
  },
  comments: {
    best: {
      candidates: ["anthropic/claude-sonnet-5.5", "anthropic/claude-sonnet-5", "anthropic/claude-sonnet-4.6"],
      why: "Best at spotting real leads, and writes replies that react to the post.",
    },
    cheapest: {
      candidates: ["anthropic/claude-haiku-5.5", "openai/gpt-6-luna", "google/gemini-3.5-flash-lite"],
      why: "Handles the lead sorting and short replies reliably for pennies.",
    },
  },
  // Live-checked 2026-10-13 on a 22-second reel (slides + voice-over): these heard the voice and described
  // the slides without inventing anything. gemini-3.5-flash-lite, qwen3.8-omni-flash and mimo-v2.6-flash
  // missed the audio, and 3.5-flash-lite once invented people — don't recommend them.
  video: {
    best: {
      candidates: ["google/gemini-3.8-flash", "google/gemini-3.7-flash", "google/gemini-3.6-flash"],
      why: "Watches closely and catches what's said and shown, word for word.",
    },
    cheapest: {
      candidates: ["google/gemini-3.1-flash-lite", "google/gemini-3.6-flash", "google/gemini-3.7-flash"],
      why: "Hears the voice-over and reads the screen reliably, for about a tenth of a cent per reel.",
    },
  },
  vision: {
    best: {
      candidates: ["anthropic/claude-sonnet-5.5", "google/gemini-3.8-flash", "anthropic/claude-sonnet-5"],
      why: "Reads the photo accurately and still writes in your voice.",
    },
    cheapest: {
      candidates: ["anthropic/claude-haiku-5.5", "openai/gpt-6-luna", "google/gemini-3.5-flash-lite"],
      why: "Accepts images and describes them well, for a fraction of a cent per post.",
    },
  },
  content: {
    best: {
      candidates: ["anthropic/claude-opus-5.5", "anthropic/claude-sonnet-5.5", "anthropic/claude-opus-5"],
      why: "The strongest writer. You only write a few captions a week, so the price barely matters.",
    },
    cheapest: {
      candidates: ["anthropic/claude-haiku-5.5", "openai/gpt-6-luna", "anthropic/claude-haiku-4.5"],
      why: "Good, on-brand captions for almost nothing.",
    },
  },
};

/** What a new connection starts with: the cheapest model that does the job well. */
export const AI_DEFAULT_KIND: AiRecommendationKind = "cheapest";

export type AiConnectMethod = "oauth" | "key";

export interface AiConnection {
  connected: boolean;
  method: AiConnectMethod | null;
  /** "sk-or-v1-…a1b2" — the key itself never leaves the server. */
  keyHint: string | null;
  /** The key's label on OpenRouter, when it has one. */
  label: string | null;
  connectedAt: number | null;
  /** From OpenRouter at read time; null when it couldn't be reached. */
  credits: AiCredits | null;
  /** Set when the stored key no longer works — the UI asks them to connect again. */
  problem: string | null;
}

export interface AiCredits {
  /** USD this key has spent. */
  usage: number;
  /** USD cap on the key, or null for no cap. */
  limit: number | null;
  /** USD left under the cap, or null for no cap. */
  remaining: number | null;
  /** OpenRouter's free tier (no credits bought yet). */
  freeTier: boolean;
}

/** One picked model, with what Grow My X needs to call it correctly. */
export interface AiModelChoice {
  id: string;
  name: string;
  vision: boolean;
  /** Accepts video. Absent on picks stored before the Video analysis job existed. */
  video?: boolean;
}

/**
 * What happens for a tenant who hasn't connected OpenRouter (server-wide, from AI_FALLBACK):
 *   "none"     — AI features stop with "Connect OpenRouter" (production)
 *   "platform" — the server's own key is used
 *   "mock"     — canned demo text (local dev and tests)
 */
export type AiFallback = "none" | "platform" | "mock";

export interface AiSettingsRes {
  connection: AiConnection;
  models: Record<AiTask, AiModelChoice | null>;
  /** Comments look at the post image (with the Image analysis model) when there is one. */
  analyzeImages: boolean;
  fallback: AiFallback;
  callsToday: number;
  dailyCap: number;
}

export interface AiSettingsPut {
  /** Model ids from GET /api/ai/models. */
  models?: Partial<Record<AiTask, string>>;
  analyzeImages?: boolean;
}

export interface AiOAuthStartReq {
  /** Where OpenRouter sends the browser back (must be on the portal's own origin). */
  callbackUrl: string;
}
export interface AiOAuthStartRes {
  url: string;
}
export interface AiOAuthCallbackReq {
  code: string;
  state: string;
}
export interface AiKeyReq {
  key: string;
}
export interface AiTestReq {
  task: AiTask;
}
export interface AiTestRes {
  ok: true;
  model: string;
  reply: string;
  ms: number;
}

/** USD for one action of `task` on `m` (approximate — reasoning tokens and caching are ignored). */
export function estimateActionCost(m: Pick<AiModelInfo, "inputPrice" | "outputPrice" | "imagePrice">, task: AiTask): number {
  const p = AI_TASK_INFO[task].profile;
  const imageTokens = m.imagePrice === null ? p.images * AI_IMAGE_TOKENS_ESTIMATE : 0;
  const imageCost = m.imagePrice === null ? 0 : p.images * m.imagePrice;
  return ((p.inputTokens + imageTokens) * m.inputPrice + p.outputTokens * m.outputPrice) / 1_000_000 + imageCost;
}

/** "$0.24", "$1.80", "$12", "< $0.01", "Free". */
export function formatUsd(usd: number): string {
  if (usd <= 0) return "Free";
  if (usd < 0.01) return "< $0.01";
  if (usd < 10) return `$${usd.toFixed(2)}`;
  return `$${Math.round(usd).toLocaleString("en-US")}`;
}

/** "$2 / $10" style price per 1M tokens. */
export function formatPerMillion(usd: number): string {
  if (usd <= 0) return "$0";
  if (usd < 0.001) return "< $0.001";
  if (usd < 0.1) return `$${trimZeros(usd.toFixed(3))}`;
  if (usd < 10) return `$${trimZeros(usd.toFixed(2))}`;
  return `$${Math.round(usd)}`;
}

/** "0.050" → "0.05", "2.00" → "2". */
const trimZeros = (s: string): string => s.replace(/\.?0+$/, "");
